/**
 * What one chat message may carry, the same limits the server enforces (server/src/pool/bodyLimits.ts:
 * AI_MAX_ATTACHMENTS, AI_MAX_ATTACHMENT_BYTES; the edge buffers the request in a memory-limited isolate).
 */
export const CHAT_MAX_ATTACHMENTS = 5
export const CHAT_MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024
/** All files of one message together, before base64. */
export const CHAT_MAX_ATTACHMENTS_TOTAL_BYTES = 8 * 1024 * 1024

export type AttachmentRejection = "too_many" | "too_large" | "total_too_large"

/** The size in bytes of a base64 payload (what an already attached file weighs). */
export function base64Bytes(data: string): number {
  const padding = data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0
  return Math.max(0, Math.floor((data.length * 3) / 4) - padding)
}

/**
 * Which of the picked files can be added to a message that already carries `existingBytes` (one entry per
 * attached file). Files are taken in order; a file that does not fit is skipped with its reason and the rest are
 * still considered.
 */
export function acceptAttachments(
  existingBytes: number[],
  picked: { name: string; size: number }[],
): { accepted: number[]; rejected: { name: string; reason: AttachmentRejection }[] } {
  let count = existingBytes.length
  let total = existingBytes.reduce((a, b) => a + b, 0)
  const accepted: number[] = []
  const rejected: { name: string; reason: AttachmentRejection }[] = []
  picked.forEach((file, i) => {
    if (count >= CHAT_MAX_ATTACHMENTS) return rejected.push({ name: file.name, reason: "too_many" })
    if (file.size > CHAT_MAX_ATTACHMENT_BYTES) return rejected.push({ name: file.name, reason: "too_large" })
    if (total + file.size > CHAT_MAX_ATTACHMENTS_TOTAL_BYTES) {
      return rejected.push({ name: file.name, reason: "total_too_large" })
    }
    accepted.push(i)
    count++
    total += file.size
  })
  return { accepted, rejected }
}
